'use client';

import { forwardRef, type ReactNode, type Ref } from 'react';
import { styled, useTheme, useThemeProps } from '@mui/material/styles';
import type { SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import varnishPrismLight from '@allenai/varnish-theme/varnishPrismLight';
import varnishPrismDark from '@allenai/varnish-theme/varnishPrismDark';

interface CodeBlockProps {
    language?: string;
    colorMode?: 'light' | 'dark';
    inline?: boolean;
    value?: string;
    children?: ReactNode;
    className?: string;
    wrapLongLines?: boolean;
}

const CodeBlockRoot = styled('div', {
    name: 'MuiCodeBlock',
    slot: 'root',
})({});

const CodeBlockInlineRoot = styled('span', {
    name: 'MuiCodeBlock',
    slot: 'root',
})({});

const CodeBlock = forwardRef<HTMLDivElement, CodeBlockProps>(function CodeBlock(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiCodeBlock' });
    const {
        language,
        colorMode = 'light',
        inline = false,
        value,
        children,
        className,
        wrapLongLines = true,
    } = props;
    const theme = useTheme();
    const languageDerived = language ?? className?.replace(/\blanguage-([^\s]+)/, '$1');

    const content = value ?? children;

    const prismTheme =
        colorMode === 'dark'
            ? (varnishPrismDark as SyntaxHighlighterProps['style'])
            : (varnishPrismLight as SyntaxHighlighterProps['style']);

    const background =
        colorMode === 'dark'
            ? (theme.palette.background as unknown as Record<string, string>).reversed
            : theme.palette.grey[100];

    if (typeof content !== 'string') {
        return (
            <CodeBlockRoot ref={ref} className={className}>
                <pre>
                    <code>{content}</code>
                </pre>
            </CodeBlockRoot>
        );
    }

    if (inline) {
        return (
            <CodeBlockInlineRoot ref={ref as Ref<HTMLSpanElement>} className={className}>
                <SyntaxHighlighter
                    language={languageDerived}
                    style={prismTheme}
                    PreTag="span"
                    wrapLongLines={wrapLongLines}
                    customStyle={{
                        display: 'inline',
                        padding: '0 4px',
                        whiteSpace: 'nowrap',
                        background,
                    }}>
                    {content}
                </SyntaxHighlighter>
            </CodeBlockInlineRoot>
        );
    }

    return (
        <CodeBlockRoot ref={ref} className={className}>
            <SyntaxHighlighter
                language={languageDerived}
                style={prismTheme}
                wrapLongLines={wrapLongLines}
                customStyle={{
                    background,
                    padding: theme.spacing(2),
                    margin: '.5em 0',
                    overflow: 'auto',
                    minWidth: 0,
                    fontSize: '.9em',
                    lineHeight: 1.2,
                    borderRadius: theme.shape.borderRadius,
                }}>
                {content}
            </SyntaxHighlighter>
        </CodeBlockRoot>
    );
});

export { CodeBlock };
export type { CodeBlockProps };

export default { CodeBlock };
